/**
 * @file Contributor dashboard UI — pages/Dashboard/Contributor/Stages/A3/components/A3Section.tsx.
 */

// src/pages/a3/components/A3Section.tsx
import React, { useEffect, useMemo, useState } from "react";
import {
  FormField,
  FormSubSection,
  type FormDataResponse,
  type PlantCapacityOption,
  type RawMaterialQualityAttributeOption,
} from "../../../../../../interfaces/formInterfaces";
import axiosClient from "../../../../../../api/axiosClient";
import { API_URLS } from "../../../../../../constants/apiUrls";
import {
  fetchManufacturingProcessesByMaterialId,
  type ManufacturingProcessRow,
} from "../../../../../../services/manufacturingProcessesPublicService";
import {
  fetchPlantCapacitiesByMaterialId,
} from "../../../../../../services/plantCatalogPublicService";
import type { FuelDensityApiRow } from "../../../../../../services/a1Service";
import type { CalorificValuePublic } from "../../../../../../services/calorificValuesPublicService";
import {
  a1StyleFuelDensityValueAndUnitFromCatalogRow,
  computeA3FuelDensitySelectState,
  discoverA3FuelFieldNames,
  isA3FuelDensityUnitField,
  isA3FuelDensityValueField,
} from "../utils/a3FuelMixAutofill";
import { A3_MAX_FUELS_PER_PROCESS } from "../utils/a3ProcessFuelLayout";
import {
  filterFuelDensityUnitOptionsToKgPerM3,
  type UnitSelectOption,
} from "../../shared/fuelMixReferenceAutofill";
import {
  type A3BackgroundTechnologySlice,
  isA3ManufacturingSpecificityFromBackgroundField,
  readBackgroundTechnologyForA3,
} from "../../../../../../utils/a3ManufacturingSpecificityField";
import {
  a3RmqValueKey,
  isA3PlantMaterialQualityField,
  rawMaterialQualityUsesCatalogAttributes,
} from "../../../../../../utils/a3RawMaterialQualityField";
import { fetchRawMaterialQualityAttributes } from "../../../../../../services/rawMaterialQualityAttributesPublicService";
import { fetchDropdownOptionsById } from "../../../../../../services/dropdownOptionsPublicService";
import FormFieldLabel from "../../../../../../components/common/FormFieldLabel";
import { resolveContributorFieldDisplayLabel } from "../../shared/contributorFieldDisplayLabels";
import { getContributorTextInputHtmlProps } from "../../../../../../utils/contributorFormFieldInputProps";
import { formatUnitOptionLabel } from "../../../../../../utils/formatUnitSymbol";
import type { ReferenceLabels } from "../../../../../../components/submission/readonlyFormDisplay";
import {
  resolveContributorDropdownDisplayLabel,
  resolveContributorUnitDisplayLabel,
} from "../../shared/unitSelectOptions";
import {
  hasA3ProcessDetailsProcessSelectionForRow,
  isA3EmbodiedCarbonLockedManufacturingProcessField,
  isA3EmbodiedEnergyLockedManufacturingProcessField,
  isA3FuelLockedManufacturingProcessField,
  isManufacturingProcessTechnologyField,
  resolveA3ProcessNameForSave,
} from "../../../../../../utils/a3ManufacturingProcessResolution";
import { resolveContributorFieldMandatory } from "../../shared/fuelMixDensityValidation";
import {
  buildEmbodiedCarbonFunctionalUnitSelectState,
  isEmbodiedCarbonFunctionalUnitFieldName,
  resolveEmbodiedCarbonFunctionalUnitDisplayLabel,
} from "../../shared/contributorPerFunctionalUnitUnits";
import {
  CF_AUTO_CALC_TITLE,
  isEmbodiedCarbonCharacterizationFactorUnitField,
  shouldDisableAutoCalculatedCharacterizationFactor,
} from "../../shared/contributorCharacterizationFactorUi";
import A3ProcessRawMaterialsMultiSelect from "./A3ProcessRawMaterialsMultiSelect";

interface Props {
  section: FormSubSection;
  values: Record<string, string>;
  /** Finished product `materials.id` (`submission.background.materialId`) — loads process dropdown options. */
  materialId?: number | null;
  /** From `submission.background.technology` — disables Manufacturing Specificity (MANUFACTURER_SPECIFICATION). */
  manufacturingSpecificityFromBackground?: {
    id: string;
    display: string;
  } | null;
  /** Submission `background` (for nested `technology` / `technologyId`). */
  submissionBackground?: A3BackgroundTechnologySlice | null;
  onChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => void;
  /** Multi-key updates (e.g. RAW_MATERIALS id + label). */
  patchValues?: (patch: Record<string, string>) => void;
  /** Raw materials declared in Background Data, scoped to the finished material catalog. */
  backgroundRawMaterialOptions?: { id: number; name: string }[];
  /** Manufacturing Process Details — number of sub-process rows (each row is one saved `A3_Process` entry). */
  processDetailsRowCount?: number;
  onAddProcessDetailsRow?: () => void;
  /** Removes the last sub-process row (minimum one row remains). */
  onRemoveLastProcessDetailsRow?: () => void;
  /** Per sub-process: how many fuel type rows (flat keys `_A3_PROCESS_FUEL_DETAILS_rowN`). */
  fuelsPerProcess?: number[];
  onAddFuelForProcess?: (processIndex: number) => void;
  onRemoveFuelForProcess?: (processIndex: number, slotIndex: number) => void;
  /** CMS form definition — used to resolve Sub Process names on fuel cards. */
  formData?: FormDataResponse | null;
  /** GET /manufacturing-processes for the finished material — maps stored ids to names on save/header. */
  manufacturingProcessesCatalog?: ManufacturingProcessRow[];
  /** GET /fuel-densities — autofill source when Fuel Mix changes (read-only display like A1/A2). */
  fuelDensityCatalog?: FuelDensityApiRow[];
  fuelDensityUnitOptions?: UnitSelectOption[];
  calorificValuesCatalog?: CalorificValuePublic[];
  /** Background finished material unit symbol (e.g. kg) for per-functional-unit compound labels. */
  backgroundFinishedMaterialUnitSymbol?: string | null;
  /** Resolved id → label maps from `GET /submissions/:id?includeFormFields=true`. */
  referenceLabels?: ReferenceLabels;
  /** Dataset Overview: disable inputs and hide add/remove controls. */
  readonly?: boolean;
  errors?: Record<string, string>;
}

function resolveStoredProcessToName(
  raw: string,
  processes: ManufacturingProcessRow[]
): string {
  if (!raw || !processes.length) return raw;
  if (processes.some((p) => p.name === raw)) return raw;
  const byId = processes.find((p) => String(p.id) === String(raw).trim());
  return byId?.name ?? raw;
}

interface UnitOption {
  id: number;
  name: string;
  symbol?: string;
  unitSymbol?: string;
  displayName?: string;
}

function sortRmqAttrs(
  list: RawMaterialQualityAttributeOption[]
): RawMaterialQualityAttributeOption[] {
  return [...list].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
}

/** Chronological raw-material label (01, 02, …) — same as A1/A2. */
const rawMaterialTripLabel = (rowIndex: number) =>
  `Raw Material ${String(rowIndex + 1).padStart(2, "0")}`;

function resolveA3FieldLabel(
  field: FormField,
  sectionCode: string,
  rowIndex: number
): string {
  if (field.type !== "RAW_MATERIALS") {
    return resolveContributorFieldDisplayLabel(field);
  }
  // Per sub-process picker — CMS label only (no separate row heading).
  if (sectionCode === "A3_PROCESS_DETAILS") {
    const base = (field.label ?? "").replace(/\s+\d{1,2}\s*$/, "").trim();
    return base || field.label;
  }
  return rawMaterialTripLabel(rowIndex);
}

function sortPlantCapacities(list: PlantCapacityOption[]): PlantCapacityOption[] {
  return [...list].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
}

function resolvePlantCapacityDisplay(
  value: string,
  options: PlantCapacityOption[]
): string {
  const id = value.trim();
  if (!id) return "—";
  const match = options.find((o) => String(o.id) === id);
  if (!match) return id;
  return String(match.capacityLabel ?? "").trim() || id;
}

/**
 * Material-scoped plant capacity — shows capacity range label only.
 */
const PlantCapacitySelect: React.FC<{
  name: string;
  value: string;
  onChange: Props["onChange"];
  inlineOptions?: PlantCapacityOption[];
  materialId: number | null;
  placeholder?: string | null;
  disabled?: boolean;
}> = ({
  name,
  value,
  onChange,
  inlineOptions,
  materialId,
  placeholder,
  disabled = false,
}) => {
  const mid =
    materialId != null && Number.isFinite(materialId) && materialId > 0
      ? materialId
      : null;
  const [options, setOptions] = useState<PlantCapacityOption[]>(() =>
    sortPlantCapacities(inlineOptions ?? [])
  );
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (inlineOptions && inlineOptions.length > 0) {
      setOptions(sortPlantCapacities(inlineOptions));
      return;
    }
    if (mid == null) {
      setOptions([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetchPlantCapacitiesByMaterialId(mid)
      .then(({ capacities }) => {
        if (cancelled) return;
        setOptions(sortPlantCapacities(capacities));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mid, inlineOptions]);

  if (mid == null) {
    return (
      <p className="a3-rmq-hint">
        Select a finished material in Background Data first.
      </p>
    );
  }

  if (loading && options.length === 0) {
    return <span className="a3-dropdown-loading">Loading options…</span>;
  }

  if (options.length === 0) {
    return (
      <p className="a3-rmq-hint a3-rmq-hint-muted">
        No plant capacity bands are defined for this material.
      </p>
    );
  }

  const selectValue = value.trim();
  const hasValue =
    selectValue && options.some((o) => String(o.id) === selectValue);
  const emptyLabel = (placeholder && String(placeholder).trim()) || "Select…";

  return (
    <select
      name={name}
      value={selectValue}
      onChange={onChange}
      disabled={disabled}
      aria-label={emptyLabel}
    >
      <option value="">{emptyLabel}</option>
      {options.map((o) => (
        <option key={o.id} value={String(o.id)}>
          {String(o.capacityLabel ?? "").trim()}
        </option>
      ))}
      {selectValue && !hasValue && (
        <option value={selectValue}>{selectValue}</option>
      )}
    </select>
  );
};

/** CMS dropdown: embedded options and/or lazy fetch by dropdownId. */
const CmsLinkedDropdownSelect: React.FC<{
  field: FormField;
  name: string;
  value: string;
  onChange: Props["onChange"];
}> = ({ field, name, value, onChange }) => {
  const [opts, setOpts] = useState<
    { id?: number; value?: string | number; label?: string }[]
  >([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const inline = field.dropdown?.options || field.dropdownValues || [];
    if (inline.length > 0) {
      setOpts(inline);
      setLoading(false);
      return;
    }
    const id = field.dropdownId != null ? Number(field.dropdownId) : NaN;
    if (!Number.isFinite(id) || id <= 0) {
      setOpts([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetchDropdownOptionsById(id)
      .then((list) => {
        if (!cancelled) {
          setOpts(
            list.map((o) => ({
              id: o.id,
              value: o.value,
              label: o.label,
            }))
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    field.id,
    field.dropdownId,
    field.dropdown?.options?.length ?? 0,
    field.dropdownValues?.length ?? 0,
  ]);

  if (loading && opts.length === 0) {
    return <span className="a3-dropdown-loading">Loading options…</span>;
  }

  const selectValue = value.trim();
  const hasValue =
    selectValue &&
    opts.some(
      (o) => String(o.value ?? "") === selectValue || String(o.id) === selectValue
    );

  return (
    <select name={name} value={selectValue} onChange={onChange}>
      <option value="">Select…</option>
      {opts.map((o) => (
        <option key={o.id ?? String(o.value)} value={String(o.value ?? "")}>
          {String(o.label ?? o.value ?? "")}
        </option>
      ))}
      {selectValue && !hasValue && (
        <option value={selectValue}>{selectValue}</option>
      )}
    </select>
  );
};

const RawMaterialQualityFieldGroup: React.FC<{
  field: FormField;
  sectionCode: string;
  rowIndex: number;
  values: Record<string, string>;
  materialId: number | null;
  onChange: Props["onChange"];
  readonly?: boolean;
}> = ({ field, sectionCode, rowIndex, values, materialId, onChange, readonly = false }) => {
  const mid =
    materialId != null && Number.isFinite(materialId) && materialId > 0
      ? materialId
      : null;
  const [attrs, setAttrs] = useState<RawMaterialQualityAttributeOption[]>(() =>
    sortRmqAttrs(field.rawMaterialQualityAttributes ?? [])
  );

  useEffect(() => {
    const inline = field.rawMaterialQualityAttributes;
    if (inline && inline.length > 0) {
      setAttrs(sortRmqAttrs(inline));
      return;
    }
    if (mid == null) {
      setAttrs([]);
      return;
    }
    let cancelled = false;
    fetchRawMaterialQualityAttributes(mid).then((list) => {
      if (!cancelled) setAttrs(sortRmqAttrs(list));
    });
    return () => {
      cancelled = true;
    };
  }, [field.id, mid, field.rawMaterialQualityAttributes?.length]);

  if (mid == null) {
    return (
      <p className="a3-rmq-hint">
        Select a finished material in Background Data first.
      </p>
    );
  }

  if (attrs.length === 0) {
    return (
      <p className="a3-rmq-hint a3-rmq-hint-muted">
        No raw material quality attributes are defined for this material.
      </p>
    );
  }

  return (
    <div className="a3-rmq-inputs">
      {attrs.map((attr) => {
        const inputName = a3RmqValueKey(
          field.name,
          attr.id,
          sectionCode,
          rowIndex
        );
        return (
          <div key={attr.id} className="a3-rmq-row">
            <FormFieldLabel label={attr.label} className="a3-rmq-sublabel" />
            <input
              {...getContributorTextInputHtmlProps(field)}
              type={readonly ? "text" : field.inputType === "number" ? "number" : "text"}
              name={inputName}
              value={values[inputName] ?? ""}
              onChange={onChange}
              readOnly={readonly}
              disabled={readonly}
              placeholder={field.placeholder ?? undefined}
              aria-label={attr.label}
              className={readonly ? "a3-readonly-field" : undefined}
            />
          </div>
        );
      })}
    </div>
  );
};

/** Fuel density value from fuel mix + /fuel-densities (auto-filled, editable like Form A1/A2). */
const A3FuelDensityFromFuelMix: React.FC<{
  formData: FormDataResponse;
  values: Record<string, string>;
  rowIndex: number;
  densityKey: string;
  densityUnitKey: string | null;
  fuelDensityCatalog: FuelDensityApiRow[];
  fuelDensityUnitOptions: UnitSelectOption[];
  a3FuelFieldNames: ReturnType<typeof discoverA3FuelFieldNames>;
  patchValues: NonNullable<Props["patchValues"]>;
  onChange: Props["onChange"];
  field: FormField;
  readonly?: boolean;
  ariaLabel: string;
}> = ({
  formData,
  values,
  rowIndex,
  densityKey,
  densityUnitKey,
  fuelDensityCatalog,
  fuelDensityUnitOptions,
  a3FuelFieldNames,
  patchValues,
  onChange,
  field,
  readonly = false,
  ariaLabel,
}) => {
  const { selectValue, shouldSync } = useMemo(
    () =>
      computeA3FuelDensitySelectState(
        formData,
        values,
        rowIndex,
        fuelDensityCatalog,
        a3FuelFieldNames,
        fuelDensityUnitOptions
      ),
    [
      formData,
      values,
      rowIndex,
      fuelDensityCatalog,
      a3FuelFieldNames,
      fuelDensityUnitOptions,
    ]
  );

  useEffect(() => {
    if (!shouldSync) return;
    if (!selectValue) {
      const p: Record<string, string> = { [densityKey]: "" };
      if (densityUnitKey) p[densityUnitKey] = "";
      patchValues(p);
      return;
    }
    const row = fuelDensityCatalog.find((r) => String(r.id) === selectValue);
    if (!row) return;
    const { densityStr, unitVal } = a1StyleFuelDensityValueAndUnitFromCatalogRow(
      row,
      fuelDensityUnitOptions
    );
    const p: Record<string, string> = { [densityKey]: densityStr };
    if (densityUnitKey) p[densityUnitKey] = unitVal;
    patchValues(p);
  }, [
    shouldSync,
    selectValue,
    densityKey,
    densityUnitKey,
    fuelDensityCatalog,
    fuelDensityUnitOptions,
    patchValues,
  ]);

  const display = String(values[densityKey] ?? "").trim();

  if (readonly) {
    return (
      <input
        type="text"
        name={densityKey}
        value={display}
        readOnly
        disabled
        className="a3-readonly-field readonly-input"
        title={display || undefined}
        aria-label={ariaLabel}
      />
    );
  }

  return (
    <input
      {...getContributorTextInputHtmlProps(field)}
      name={densityKey}
      value={values[densityKey] ?? ""}
      onChange={onChange}
      aria-label={ariaLabel}
    />
  );
};

const A3Section: React.FC<Props> = ({
  section,
  values,
  materialId = null,
  manufacturingSpecificityFromBackground = null,
  submissionBackground = null,
  onChange,
  patchValues,
  backgroundRawMaterialOptions = [],
  processDetailsRowCount = 1,
  onAddProcessDetailsRow,
  onRemoveLastProcessDetailsRow,
  fuelsPerProcess,
  onAddFuelForProcess,
  onRemoveFuelForProcess,
  formData = null,
  manufacturingProcessesCatalog = [],
  fuelDensityCatalog = [],
  fuelDensityUnitOptions = [],
  calorificValuesCatalog = [],
  backgroundFinishedMaterialUnitSymbol = null,
  referenceLabels,
  readonly = false,
  errors = {},
}) => {
  const code = section.code!;
  const a3FuelFieldNames = useMemo(
    () => discoverA3FuelFieldNames(formData ?? null),
    [formData]
  );
  const resolveFieldMandatory = (field: FormField, rowIndex: number) =>
    resolveContributorFieldMandatory({
      field,
      sectionCode: code,
      rowIndex,
      values,
      formData,
      fuelDensityCatalog,
      calorificValuesCatalog,
    });
  /** Process + Plant details: 2 fields per row (2×2); other A3 sections stay 3 columns. */
  const useTwoColumnGrid =
    code === "A3_PROCESS_DETAILS" ||
    code === "A3_MANUFACTURING_PLANT_DETAILS";
  const gridColumns = useTwoColumnGrid ? 2 : 3;
  const [unitOptionsMap, setUnitOptionsMap] = useState<Record<number, UnitOption[]>>({});
  const [allUnitsFlat, setAllUnitsFlat] = useState<UnitOption[]>([]);
  const [manufacturingProcesses, setManufacturingProcesses] = useState<
    ManufacturingProcessRow[]
  >([]);

  // Fetch units for all UNIT fields in this section
  useEffect(() => {
    const fetchUnits = async () => {
      const unitFields = section.fields.filter((f) => f.type === "UNIT" && f.unitPropertyId);
      const promises = unitFields.map(async (field) => {
        if (!field.unitPropertyId) return;
        try {
          const res = await axiosClient.get(API_URLS.UNITS.BY_PROPERTY(field.unitPropertyId));
          if (res.data?.success && res.data?.data) {
            const units = Array.isArray(res.data.data) ? res.data.data : [];
            return { fieldId: field.id, units };
          }
        } catch (err) {
          console.warn(`Failed to fetch units for property ${field.unitPropertyId}:`, err);
        }
        return { fieldId: field.id, units: [] };
      });

      const results = await Promise.all(promises);
      const newMap: Record<number, UnitOption[]> = {};
      results.forEach((result) => {
        if (result) {
          newMap[result.fieldId] = result.units;
        }
      });
      setUnitOptionsMap(newMap);
    };

    if (section.fields.some((f) => f.type === "UNIT" && f.unitPropertyId)) {
      fetchUnits();
    }
  }, [section]);

  // Fetch ALL units once (needed for some unit fields like characterizationFactorUnit)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axiosClient.get("/units");
        const raw = res?.data?.data ?? res?.data;

        const flat: UnitOption[] = [];
        const consumeUnit = (u: any) => {
          if (!u?.id) return;
          flat.push({
            id: Number(u.id),
            name: u.name || u.displayName || u.unitSymbol || u.symbol || "",
            symbol: u.symbol || u.unitSymbol,
            unitSymbol: u.unitSymbol,
            displayName: u.displayName,
          });
        };

        if (Array.isArray(raw)) {
          raw.forEach((propertyOrUnit: any) => {
            if (Array.isArray(propertyOrUnit?.units)) {
              propertyOrUnit.units.forEach(consumeUnit);
            } else {
              consumeUnit(propertyOrUnit);
            }
          });
        } else if (raw && Array.isArray((raw as any).units)) {
          (raw as any).units.forEach(consumeUnit);
        }

        if (!cancelled) setAllUnitsFlat(flat.filter((u) => Number.isFinite(u.id)));
      } catch {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const needsManufacturingProcessOptions =
    section.fields.some((f) => f.type === "MANUFACTURING_PROCESS") ||
    (code === "A3_PROCESS_DETAILS" &&
      section.fields.some((f) => isManufacturingProcessTechnologyField(f, code)));

  useEffect(() => {
    if (!needsManufacturingProcessOptions) {
      setManufacturingProcesses([]);
      return;
    }
    const mid =
      materialId != null && Number.isFinite(materialId) && materialId > 0
        ? materialId
        : null;
    if (mid == null) {
      setManufacturingProcesses([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const list = await fetchManufacturingProcessesByMaterialId(mid);
        if (!cancelled) setManufacturingProcesses(list);
      } catch (e) {
        console.warn("[A3] manufacturing-processes fetch failed:", e);
        if (!cancelled) setManufacturingProcesses([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [code, materialId, needsManufacturingProcessOptions]);

  const getName = (f: FormField, rowIndex: number) =>
    `${f.name}_${code}_row${rowIndex}`;

  const renderInput = (
    f: FormField,
    rowIndex: number,
    opts?: { fuelProcessRowIndex?: number }
  ) => {
    const name = getName(f, rowIndex);
    const value = values[name] ?? "";
    const cfLocked = shouldDisableAutoCalculatedCharacterizationFactor(code, f, {
      values,
      rowIndex,
      formData,
      fuelsPerProcess: fuelsPerProcess ?? [],
      processRowCount: processDetailsRowCount ?? 1,
    });

    if (
      cfLocked &&
      !isEmbodiedCarbonCharacterizationFactorUnitField(f, code, formData)
    ) {
      return (
        <input
          type="text"
          inputMode="decimal"
          name={name}
          value={value}
          readOnly
          disabled
          className="a3-readonly-synced-field"
          title={CF_AUTO_CALC_TITLE}
        />
      );
    }

    if (
      cfLocked &&
      isEmbodiedCarbonCharacterizationFactorUnitField(f, code, formData)
    ) {
      const unitsByProperty = unitOptionsMap[f.id] || [];
      const units =
        unitsByProperty.length > 0 ? unitsByProperty : allUnitsFlat;
      const display =
        resolveContributorUnitDisplayLabel({
          selected: String(value ?? ""),
          propertyUnits: units,
          allUnitsFlat,
          dropdownOptions: f.dropdown?.options || f.dropdownValues || [],
          referenceUnits: referenceLabels?.units,
        }) || "—";
      return (
        <input
          type="text"
          name={name}
          value={display}
          readOnly
          disabled
          className="a3-readonly-synced-field"
          title={CF_AUTO_CALC_TITLE}
          aria-label={f.label ?? undefined}
        />
      );
    }

    if (
      rowIndex === 0 &&
      isA3ManufacturingSpecificityFromBackgroundField(f, code, section.name)
    ) {
      const labelKey = `${name}_label`;
      const fromSubmission = readBackgroundTechnologyForA3(
        submissionBackground ?? undefined
      );
      const display =
        String(values[labelKey] ?? "").trim() ||
        String(fromSubmission.display || "").trim() ||
        String(fromSubmission.id || "").trim() ||
        String(manufacturingSpecificityFromBackground?.display ?? "").trim() ||
        String(manufacturingSpecificityFromBackground?.id ?? "").trim() ||
        String(value ?? "").trim() ||
        "—";
      return (
        <input
          type="text"
          name={name}
          value={display}
          readOnly
          disabled
          title={display}
          className="a3-readonly-background-field"
        />
      );
    }

    if (
      code === "A3_PROCESS_FUEL_DETAILS" &&
      formData &&
      isA3FuelLockedManufacturingProcessField(f, code)
    ) {
      const processRow = opts?.fuelProcessRowIndex ?? rowIndex;
      const display =
        resolveA3ProcessNameForSave(
          formData,
          values,
          processRow,
          manufacturingProcessesCatalog
        ) || "—";
      return (
        <input
          type="text"
          readOnly
          disabled
          value={display}
          title={display}
          className="a3-readonly-synced-field"
          aria-label={f.label ?? "Manufacturing process"}
        />
      );
    }

    if (
      code === "A3_EMBODIED_ENERGY_DETAILS" &&
      formData &&
      isA3EmbodiedEnergyLockedManufacturingProcessField(f, code)
    ) {
      const display =
        resolveA3ProcessNameForSave(
          formData,
          values,
          rowIndex,
          manufacturingProcessesCatalog
        ) || "—";
      return (
        <input
          type="text"
          readOnly
          disabled
          value={display}
          title={display}
          className="a3-readonly-synced-field"
          aria-label={f.label ?? "Manufacturing process"}
        />
      );
    }

    if (
      code === "A3_EMBODIED_CARBON_DETAILS" &&
      formData &&
      isA3EmbodiedCarbonLockedManufacturingProcessField(f, code)
    ) {
      const display =
        resolveA3ProcessNameForSave(
          formData,
          values,
          rowIndex,
          manufacturingProcessesCatalog
        ) || "—";
      return (
        <input
          type="text"
          readOnly
          disabled
          value={display}
          title={display}
          className="a3-readonly-synced-field"
          aria-label={f.label ?? "Manufacturing process"}
        />
      );
    }

    if (isA3PlantMaterialQualityField(f, code)) {
      return (
        <RawMaterialQualityFieldGroup
          field={f}
          sectionCode={code}
          rowIndex={rowIndex}
          values={values}
          materialId={materialId ?? null}
          onChange={onChange}
          readonly={readonly}
        />
      );
    }

    if (readonly) {
      if (f.type === "RAW_MATERIALS" && code === "A3_PROCESS_DETAILS") {
        const labelKey = `${name}_label`;
        const display =
          String(values[labelKey] ?? "").trim() ||
          String(value ?? "").trim() ||
          "—";
        return (
          <input
            type="text"
            name={name}
            value={display}
            readOnly
            disabled
            className="a3-readonly-field"
            title={display}
            aria-label={f.label ?? undefined}
          />
        );
      }

      if (f.type === "UNIT") {
        if (isEmbodiedCarbonFunctionalUnitFieldName(f.name)) {
          const display =
            resolveEmbodiedCarbonFunctionalUnitDisplayLabel(
              backgroundFinishedMaterialUnitSymbol ?? null,
              String(value ?? "")
            ) || "—";
          return (
            <input
              type="text"
              name={name}
              value={display}
              readOnly
              disabled
              className="a3-readonly-field"
              title={display}
              aria-label={f.label ?? undefined}
            />
          );
        }

        const unitsByProperty = unitOptionsMap[f.id] || [];
        const units =
          unitsByProperty.length > 0 ? unitsByProperty : allUnitsFlat;
        const display =
          resolveContributorUnitDisplayLabel({
            selected: String(value ?? ""),
            propertyUnits: units,
            allUnitsFlat,
            dropdownOptions: f.dropdown?.options || f.dropdownValues || [],
            referenceUnits: referenceLabels?.units,
          }) || "—";
        return (
          <input
            type="text"
            name={name}
            value={display}
            readOnly
            disabled
            className="a3-readonly-field"
            title={display}
            aria-label={f.label ?? undefined}
          />
        );
      }

      if (f.type === "DROPDOWN" || f.type === "RAW_MATERIAL_QUALITY") {
        if (
          f.type === "RAW_MATERIAL_QUALITY" &&
          rawMaterialQualityUsesCatalogAttributes(f, code)
        ) {
          return (
            <RawMaterialQualityFieldGroup
              field={f}
              sectionCode={code}
              rowIndex={rowIndex}
              values={values}
              materialId={materialId ?? null}
              onChange={onChange}
              readonly
            />
          );
        }
        const isMaterialQuality =
          f.type === "RAW_MATERIAL_QUALITY" ||
          (f.name || "").toLowerCase().includes("materialquality");
        const display =
          resolveContributorDropdownDisplayLabel({
            selected: String(value ?? ""),
            options: f.dropdown?.options || f.dropdownValues || [],
            referenceMap: isMaterialQuality
              ? referenceLabels?.rawMaterialQualityAttributes
              : referenceLabels?.manufacturingProcesses,
          }) || "—";
        return (
          <input
            type="text"
            name={name}
            value={display}
            readOnly
            disabled
            className="a3-readonly-field"
            title={display}
            aria-label={f.label ?? undefined}
          />
        );
      }

      if (f.type === "PLANT_CAPACITY") {
        const labelKey = `${name}_label`;
        const inline = f.plantCapacities ?? [];
        const display =
          String(values[labelKey] ?? "").trim() ||
          resolvePlantCapacityDisplay(String(value ?? ""), inline);
        return (
          <input
            type="text"
            name={name}
            value={display}
            readOnly
            disabled
            className="a3-readonly-field"
            title={display}
            aria-label={f.label ?? undefined}
          />
        );
      }

      if (f.type === "MANUFACTURING_PROCESS") {
        const processCatalog = manufacturingProcessesCatalog.length
          ? manufacturingProcessesCatalog
          : manufacturingProcesses;
        const legacyName = String(
          values[`processName_A3_01_A3_PROCESS_DETAILS_row${rowIndex}`] ?? ""
        ).trim();
        const display =
          (formData
            ? resolveA3ProcessNameForSave(
                formData,
                values,
                rowIndex,
                processCatalog
              )
            : legacyName) ||
          String(value ?? "").trim() ||
          "—";
        return (
          <input
            type="text"
            name={name}
            value={display}
            readOnly
            disabled
            className="a3-readonly-field"
            title={display}
            aria-label={f.label ?? undefined}
          />
        );
      }

      const display = String(value ?? "").trim() || "—";
      return (
        <input
          type="text"
          name={name}
          value={display}
          readOnly
          disabled
          className="a3-readonly-field"
          title={display}
          aria-label={f.label ?? undefined}
        />
      );
    }

    if (
      code === "A3_PROCESS_FUEL_DETAILS" &&
      formData &&
      patchValues &&
      fuelDensityCatalog.length > 0 &&
      isA3FuelDensityValueField(f)
    ) {
      const densityKey = getName(f, rowIndex);
      const unitFieldName = a3FuelFieldNames.densityUnit;
      const densityUnitKey = unitFieldName
        ? `${unitFieldName}_${code}_row${rowIndex}`
        : null;
      return (
        <A3FuelDensityFromFuelMix
          formData={formData}
          values={values}
          rowIndex={rowIndex}
          densityKey={densityKey}
          densityUnitKey={densityUnitKey}
          fuelDensityCatalog={fuelDensityCatalog}
          fuelDensityUnitOptions={fuelDensityUnitOptions}
          a3FuelFieldNames={a3FuelFieldNames}
          patchValues={patchValues}
          onChange={onChange}
          field={f}
          readonly={readonly}
          ariaLabel={f.label ?? "Fuel density"}
        />
      );
    }

    switch (f.type) {
      case "READONLY":
        return <input readOnly value={value} />;

      case "MANUFACTURING_PROCESS": {
        const inline = f.manufacturingProcesses;
        const list: ManufacturingProcessRow[] =
          inline && inline.length > 0
            ? inline.map((p) => ({
                id: p.id,
                materialId: Number(p.materialId ?? materialId ?? 0),
                name: String(p.name ?? "").trim(),
                sortOrder: Number(p.sortOrder ?? 0),
              }))
            : manufacturingProcesses;
        const byId = (idStr: string) => list.find((p) => String(p.id) === idStr);
        let selectValue = value.trim();
        if (selectValue && !byId(selectValue) && list.length) {
          const byName = list.find((p) => p.name === selectValue);
          if (byName) selectValue = String(byName.id);
        }
        return (
          <select name={name} value={selectValue} onChange={onChange}>
            <option value="">Select…</option>
            {list.map((p) => (
              <option key={p.id} value={String(p.id)}>
                {p.name}
              </option>
            ))}
            {selectValue &&
              !list.some(
                (p) => String(p.id) === selectValue || p.name === selectValue
              ) && (
                <option value={selectValue}>{selectValue}</option>
              )}
          </select>
        );
      }

      case "PLANT_CAPACITY":
        return (
          <PlantCapacitySelect
            name={name}
            value={value}
            onChange={onChange}
            inlineOptions={f.plantCapacities}
            materialId={materialId ?? null}
            placeholder={f.placeholder}
          />
        );

      case "DROPDOWN": {
        const useApiProcesses =
          isManufacturingProcessTechnologyField(f, code) &&
          manufacturingProcesses.length > 0;
        const selectValue = useApiProcesses
          ? resolveStoredProcessToName(value, manufacturingProcesses)
          : value;

        if (useApiProcesses) {
          return (
            <select name={name} value={selectValue} onChange={onChange}>
              <option value="">Select…</option>
              {manufacturingProcesses.map((p) => (
                <option key={p.id} value={p.name}>
                  {p.name}
                </option>
              ))}
              {selectValue &&
                !manufacturingProcesses.some((p) => p.name === selectValue) && (
                  <option value={selectValue}>{selectValue}</option>
                )}
            </select>
          );
        }

        return (
          <CmsLinkedDropdownSelect
            field={f}
            name={name}
            value={value}
            onChange={onChange}
          />
        );
      }

      case "RAW_MATERIAL_QUALITY":
        if (!rawMaterialQualityUsesCatalogAttributes(f, code)) {
          return (
            <CmsLinkedDropdownSelect
              field={f}
              name={name}
              value={value}
              onChange={onChange}
            />
          );
        }
        return (
          <RawMaterialQualityFieldGroup
            field={f}
            sectionCode={code}
            rowIndex={rowIndex}
            values={values}
            materialId={materialId ?? null}
            onChange={onChange}
          />
        );

      case "RAW_MATERIALS": {
        if (code === "A3_PROCESS_DETAILS" && patchValues) {
          const labelKey = `${name}_label`;
          const opts = backgroundRawMaterialOptions;
          if (opts.length === 0) {
            return (
              <p className="a3-rmq-hint">
                Declare raw materials for this product in Background Data first.
              </p>
            );
          }

          return (
            <A3ProcessRawMaterialsMultiSelect
              name={name}
              value={value}
              labelValue={values[labelKey] ?? ""}
              options={opts}
              placeholder="Select raw materials"
              onChange={patchValues}
            />
          );
        }
        const labelKeyRm = `${name}_label`;
        const fallbackLabelRm =
          values["material_A3_label"];
        const displayRm =
          values[labelKeyRm] || fallbackLabelRm || value;
        return <input readOnly value={displayRm} />;
      }

      case "MATERIALS":
      case "SUBMATERIALS": {
        const labelKey = `${name}_label`;
        const fallbackLabel =
          f.type === "SUBMATERIALS"
            ? values["subMaterial_A3_label"]
            : values["material_A3_label"];
        const display =
          values[labelKey] || fallbackLabel || value;
        return <input readOnly value={display} />;
      }

      case "UNIT": {
        if (isEmbodiedCarbonFunctionalUnitFieldName(f.name)) {
          const { options, selectValue } =
            buildEmbodiedCarbonFunctionalUnitSelectState(
              backgroundFinishedMaterialUnitSymbol ?? null,
              String(value ?? "")
            );
          return (
            <select name={name} value={selectValue} onChange={onChange}>
              <option value="">Select Unit…</option>
              {options.map((o) => (
                <option key={String(o.value)} value={String(o.value)}>
                  {o.label}
                </option>
              ))}
            </select>
          );
        }

        // Use fetched units if available, otherwise fallback to dropdown options
        const unitsByProperty = unitOptionsMap[f.id] || [];
        // If property-based units are empty (or unitPropertyId missing), fall back to all units
        // so unit dropdowns always have options.
        const units =
          unitsByProperty.length > 0 ? unitsByProperty : allUnitsFlat;
        const rawOptions =
          units.length > 0
            ? units.map((unit: UnitOption) => ({
                id: unit.id,
                value: String(unit.id),
                label: formatUnitOptionLabel(unit),
              }))
            : (f.dropdown?.options || f.dropdownValues || []);
        const options = isA3FuelDensityUnitField(f)
          ? filterFuelDensityUnitOptionsToKgPerM3(
              rawOptions.map((o) => ({
                value: String(o.value ?? o.id ?? ""),
                label: String(o.label ?? o.value ?? ""),
              }))
            )
          : rawOptions;
        const unitSelectValue =
          isA3FuelDensityUnitField(f) &&
          !options.some((o) => String(o.value) === String(value ?? ""))
            ? String(options[0]?.value ?? "")
            : value;

        return (
          <select name={name} value={unitSelectValue} onChange={onChange}>
            <option value="">Select Unit…</option>
            {options.map((o) => (
              <option key={String(o.value)} value={String(o.value)}>
                {String(o.label ?? o.value)}
              </option>
            ))}
          </select>
        );
      }

      default:
        return (
          <input
            {...getContributorTextInputHtmlProps(f)}
            name={name}
            value={value}
            onChange={onChange}
          />
        );
    }
  };

  const gridClassName =
    useTwoColumnGrid
      ? "a1-raw-extraction-grid a3-grid-2-cols"
      : "a1-raw-extraction-grid";

  const renderFieldGrid = (rowIndex: number) => (
    <>
      {section.fields.map((f) => {
        const fieldName = getName(f, rowIndex);
        const fieldError = errors[fieldName];
        return (
          <div
            key={`${f.id}_r${rowIndex}`}
            className={`field-cell ${fieldError ? "field-has-error" : ""}`}
          >
            <FormFieldLabel
              label={resolveA3FieldLabel(f, code, rowIndex)}
              required={resolveFieldMandatory(f, rowIndex)}
              infoText={f.infoText}
            />
            {renderInput(f, rowIndex)}
            {fieldError ? (
              <span className="error-message">{fieldError}</span>
            ) : null}
          </div>
        );
      })}
      {section.fields.length % gridColumns !== 0 &&
        Array.from({
          length: gridColumns - (section.fields.length % gridColumns),
        }).map((_, i) => (
          <div key={`pad-${rowIndex}-${i}`} className="field-cell empty-cell" />
        ))}
    </>
  );

  if (
    code === "A3_PROCESS_FUEL_DETAILS" &&
    formData &&
    processDetailsRowCount >= 1
  ) {
    const counts =
      fuelsPerProcess && fuelsPerProcess.length === processDetailsRowCount
        ? fuelsPerProcess
        : Array.from({ length: processDetailsRowCount }, () => 1);
    let flat = 0;
    return (
      <div className="a3-fuel-multi">
        <p className="a3-fuel-types-note">
          Up to {A3_MAX_FUELS_PER_PROCESS} fuel types can be added per
          sub-process.
        </p>
        {Array.from({ length: processDetailsRowCount }).map(
          (_, processIndex) => {
            const n = Math.max(1, counts[processIndex] ?? 1);
            return (
              <div
                key={`a3-fuel-sub-${processIndex}`}
                className="a3-fuel-process-card"
              >
                {processDetailsRowCount > 1 && (
                  <h4 className="a3-fuel-process-card-header">
                    Sub-process {processIndex + 1}
                  </h4>
                )}
                {Array.from({ length: n }).map((_, slotIndex) => {
                  const rowIdx = flat;
                  flat += 1;
                  const canRemoveFuel = n > 1 && onRemoveFuelForProcess;
                  return (
                    <React.Fragment key={`a3-fuel-p${processIndex}-s${slotIndex}`}>
                      <div className="a3-fuel-slot-header">
                        <h5 className="a3-fuel-slot-title">
                          Fuel type {String(slotIndex + 1).padStart(2, "0")}
                        </h5>
                        {canRemoveFuel ? (
                          <button
                            type="button"
                            className="a3-remove-fuel-btn"
                            onClick={() =>
                              onRemoveFuelForProcess!(
                                processIndex,
                                slotIndex
                              )
                            }
                          >
                            Remove
                          </button>
                        ) : null}
                      </div>
                      <fieldset
                        className="a3-fuel-fieldset"
                        disabled={
                          !hasA3ProcessDetailsProcessSelectionForRow(
                            formData,
                            values,
                            processIndex
                          )
                        }
                      >
                        <div className={gridClassName}>
                          {section.fields.map((f) => {
                            const fieldName = getName(f, rowIdx);
                            const fieldError = errors[fieldName];
                            return (
                            <div
                              key={`${f.id}_r${rowIdx}`}
                              className={`field-cell ${fieldError ? "field-has-error" : ""}`}
                            >
                              <FormFieldLabel
                                label={resolveA3FieldLabel(f, code, rowIdx)}
                                required={resolveFieldMandatory(f, rowIdx)}
                                infoText={f.infoText}
                              />
                              {renderInput(f, rowIdx, {
                                fuelProcessRowIndex: processIndex,
                              })}
                              {fieldError ? (
                                <span className="error-message">{fieldError}</span>
                              ) : null}
                            </div>
                          );
                          })}
                          {section.fields.length % gridColumns !== 0 &&
                            Array.from({
                              length:
                                gridColumns -
                                (section.fields.length % gridColumns),
                            }).map((_, i) => (
                              <div
                                key={`pad-fuel-${rowIdx}-${i}`}
                                className="field-cell empty-cell"
                              />
                            ))}
                        </div>
                      </fieldset>
                      {slotIndex < n - 1 ? (
                        <div className="a3-fuel-slot-separator" />
                      ) : null}
                    </React.Fragment>
                  );
                })}
                {onAddFuelForProcess &&
                (counts[processIndex] ?? 1) < A3_MAX_FUELS_PER_PROCESS ? (
                  <div className="a3-add-fuel-wrap">
                    <button
                      type="button"
                      className="a3-add-subprocess-btn"
                      onClick={() => onAddFuelForProcess(processIndex)}
                    >
                      Add fuel type
                    </button>
                  </div>
                ) : null}
              </div>
            );
          }
        )}
      </div>
    );
  }

  if (
    code === "A3_EMBODIED_CARBON_DETAILS" &&
    formData &&
    processDetailsRowCount >= 1
  ) {
    return (
      <div className="a3-carbon-multi">
        {Array.from({ length: processDetailsRowCount }).map((_, rowIndex) => (
          <div
            key={`a3-carbon-sub-${rowIndex}`}
            className="a3-carbon-process-card"
          >
            {processDetailsRowCount > 1 && (
              <h4 className="a3-carbon-process-card-header">
                Sub-process {rowIndex + 1}
              </h4>
            )}
            <fieldset
              className="a3-carbon-fieldset"
              disabled={
                !hasA3ProcessDetailsProcessSelectionForRow(
                  formData,
                  values,
                  rowIndex
                )
              }
            >
              <div className={gridClassName}>{renderFieldGrid(rowIndex)}</div>
            </fieldset>
          </div>
        ))}
      </div>
    );
  }

  if (
    code === "A3_EMBODIED_ENERGY_DETAILS" &&
    formData &&
    processDetailsRowCount >= 1
  ) {
    return (
      <div className="a3-energy-multi">
        {Array.from({ length: processDetailsRowCount }).map((_, rowIndex) => (
          <div
            key={`a3-energy-sub-${rowIndex}`}
            className="a3-energy-process-card"
          >
            {processDetailsRowCount > 1 && (
              <h4 className="a3-energy-process-card-header">
                Sub-process {rowIndex + 1}
              </h4>
            )}
            <fieldset
              className="a3-energy-fieldset"
              disabled={
                !hasA3ProcessDetailsProcessSelectionForRow(
                  formData,
                  values,
                  rowIndex
                )
              }
            >
              <div className={gridClassName}>
                {section.fields.map((f) => (
                  <div key={`${f.id}_r${rowIndex}`} className="field-cell">
                    <FormFieldLabel
                      label={resolveA3FieldLabel(f, code, rowIndex)}
                      required={resolveFieldMandatory(f, rowIndex)}
                      infoText={f.infoText}
                    />
                    {renderInput(f, rowIndex)}
                  </div>
                ))}
                {section.fields.length % gridColumns !== 0 &&
                  Array.from({
                    length: gridColumns - (section.fields.length % gridColumns),
                  }).map((_, i) => (
                    <div
                      key={`pad-energy-${rowIndex}-${i}`}
                      className="field-cell empty-cell"
                    />
                  ))}
              </div>
            </fieldset>
          </div>
        ))}
      </div>
    );
  }

  if (
    code === "A3_PROCESS_DETAILS" &&
    onAddProcessDetailsRow &&
    processDetailsRowCount >= 1
  ) {
    return (
      <div className="a3-process-details-multi">
        {Array.from({ length: processDetailsRowCount }).map((_, rowIndex) => (
          <div
            key={`a3-process-sub-${rowIndex}`}
            className="a3-process-detail-row-block"
          >
            {processDetailsRowCount > 1 && (
              <h4 className="a3-process-detail-row-title">
                Sub-process {rowIndex + 1}
              </h4>
            )}
            <div className={gridClassName}>{renderFieldGrid(rowIndex)}</div>
          </div>
        ))}
        <div className="a3-subprocess-actions">
          {onRemoveLastProcessDetailsRow && (
            <button
              type="button"
              className="a3-remove-subprocess-btn"
              onClick={onRemoveLastProcessDetailsRow}
              disabled={processDetailsRowCount <= 1}
            >
              Delete sub-process
            </button>
          )}
          <button
            type="button"
            className="a3-add-subprocess-btn"
            onClick={onAddProcessDetailsRow}
          >
            Add sub-process
          </button>
        </div>
      </div>
    );
  }

  return <div className={gridClassName}>{renderFieldGrid(0)}</div>;
};

/** React component — A3Section. */
export default A3Section;